/**
 * ONE suggested action per email (Mailroom v2 PR 6) — the approved mockup's
 * "each row shows what it is and one button". Pure, so it is unit-tested and
 * the row, the right-click menu and the reader's Next-step bar can never
 * disagree about what to do next.
 */
import type { MailType } from "../../lib/mail-count";

export const NEXT_STEP_KINDS = ["reply", "attach", "intake", "done", "open", "nudge"] as const;
export type NextStepKind = (typeof NEXT_STEP_KINDS)[number];

export interface NextStep {
    kind: NextStepKind;
    /** The button's word. */
    label: string;
    /** One short sentence for the reader's Next-step bar (7th-grade words). */
    why: string;
}

export interface NextStepInput {
    mailType?: MailType | null;
    workState: "needs_work" | "working" | "handled" | "fyi";
    weRepliedLast: boolean;
    attachmentCount?: number;
}

/** Which bucket a row sits in on the list. */
export type RowGroup = "on_us" | "waiting" | "quiet" | "closed";

export function rowGroup(t: NextStepInput): RowGroup {
    if (t.workState === "handled" || t.workState === "fyi") return "closed";
    if (t.mailType === "fyi") return "quiet";
    if (t.weRepliedLast) return "waiting";
    return "on_us";
}

export function nextStep(t: NextStepInput): NextStep {
    const hasFiles = (t.attachmentCount ?? 0) > 0;
    if (t.workState === "handled" || t.workState === "fyi") {
        return { kind: "open", label: "Open", why: "This one is closed. Open it to read or reopen it." };
    }
    if (t.weRepliedLast) {
        return {
            kind: "nudge",
            label: "Follow up",
            why: "We answered last. Nothing is needed unless they stay quiet.",
        };
    }
    switch (t.mailType) {
        case "work_order":
            return hasFiles
                ? {
                      kind: "intake",
                      label: "To Intake Desk",
                      why: "A new work order. Send the PDF to the Intake Desk to make the job.",
                  }
                : { kind: "attach", label: "Attach to job", why: "A work order with no PDF. Attach it to its job." };
        case "customer":
            return { kind: "reply", label: "Reply", why: "A customer wrote to us. Answer them." };
        case "city":
            return hasFiles
                ? {
                      kind: "attach",
                      label: "Attach to job",
                      why: "City mail with a file. Put it on the job it is about.",
                  }
                : { kind: "reply", label: "Reply", why: "The city wrote to us. Answer or handle the request." };
        case "bounce":
            return {
                kind: "open",
                label: "See what bounced",
                why: "One of our emails did not arrive. Fix the address and send it again.",
            };
        case "vendor":
            return hasFiles
                ? { kind: "attach", label: "Attach to job", why: "A vendor invoice or PO. Put it on its job." }
                : { kind: "done", label: "Done", why: "Vendor mail with nothing to file." };
        case "fyi":
            return { kind: "done", label: "Done", why: "For your information only. Nothing to do." };
        default:
            return { kind: "reply", label: "Reply", why: "Someone wrote to us. Answer them." };
    }
}
